import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import {
  applyWizardAnswersToCalculation,
  extractTemplateWizardQuestions,
} from "./utils/template-wizard";
import "./calculations/module-builder.css";

const ACCENT = "#1463C7";

function ProductListWizardControl({ question, value, onChange, t }) {
  const options = Array.isArray(question.options) ? question.options : [];
  const isSingle = question.mode === "single";
  const selected = isSingle
    ? value != null && value !== ""
      ? [String(value)]
      : []
    : Array.isArray(value)
      ? value.map(String)
      : [];

  const toggle = (optionValue, required) => {
    const key = String(optionValue);
    if (isSingle) {
      onChange(key);
      return;
    }
    const set = new Set(selected);
    if (set.has(key)) {
      if (required) return;
      set.delete(key);
    } else {
      set.add(key);
    }
    onChange(Array.from(set));
  };

  return (
    <div className="offer-module-builder__field">
      <label>{question.label}</label>
      <p className="mb-2 text-xs text-slate-500">
        {isSingle
          ? t("offer.calculation.product_list.wizard_single_hint")
          : t("offer.calculation.product_list.wizard_multi_hint")}
      </p>
      <ul className="space-y-2">
        {options.map((opt) => {
          const key = String(opt.value);
          const checked = selected.includes(key);
          return (
            <li key={key}>
              <label className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 bg-white px-2 py-1.5 text-sm">
                <input
                  type={isSingle ? "radio" : "checkbox"}
                  name={question.id}
                  checked={checked}
                  disabled={Boolean(opt.required) && checked && !isSingle}
                  onChange={() => toggle(opt.value, opt.required)}
                />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-slate-800">{opt.label}</span>
                  {opt.required ? (
                    <span className="ml-1 text-xs text-amber-700">
                      ({t("offer.calculation.product_list.required")})
                    </span>
                  ) : null}
                  {opt.sales_price != null ? (
                    <span className="mt-0.5 block text-xs text-slate-500">
                      {t("offer.calculation.workspace.col.sales_price")}:{" "}
                      {opt.sales_price}
                    </span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function WizardFieldControl({ question, value, onChange, t }) {
  const fieldType = question.fieldType || "text";
  const label =
    question.kind === "node"
      ? `${question.nodeTitle} — ${question.label}`
      : question.label;
  const labelWithUnit =
    question.unit && fieldType !== "percent"
      ? `${label} (${question.unit})`
      : label;

  if (fieldType === "product_list") {
    return (
      <ProductListWizardControl
        question={question}
        value={value}
        onChange={onChange}
        t={t}
      />
    );
  }

  if (fieldType === "boolean") {
    const isEnabled = value !== false && value !== "false";
    return (
      <div className="offer-module-builder__field">
        <label htmlFor={question.id}>{labelWithUnit}</label>
        <button
          id={question.id}
          type="button"
          className={[
            "offer-module-builder__boolean-button",
            isEnabled ? "is-active" : "is-inactive",
          ].join(" ")}
          onClick={() => onChange(!isEnabled)}
        >
          {isEnabled
            ? t("offer.calculation.module.enabled")
            : t("offer.calculation.module.disabled")}
        </button>
      </div>
    );
  }

  if (fieldType === "select" && Array.isArray(question.options)) {
    return (
      <div className="offer-module-builder__field">
        <label htmlFor={question.id}>{labelWithUnit}</label>
        <div className="offer-module-builder__field-row">
          <select
            id={question.id}
            aria-label={labelWithUnit}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          >
            {question.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  }

  if (fieldType === "textarea") {
    return (
      <div className="offer-module-builder__field">
        <label htmlFor={question.id}>{labelWithUnit}</label>
        <div className="offer-module-builder__field-row">
          <textarea
            id={question.id}
            aria-label={labelWithUnit}
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      </div>
    );
  }

  if (fieldType === "number" || fieldType === "percent") {
    return (
      <div className="offer-module-builder__field">
        <label htmlFor={question.id}>{labelWithUnit}</label>
        <div className="offer-module-builder__field-row">
          <input
            id={question.id}
            aria-label={labelWithUnit}
            type="number"
            inputMode="decimal"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
          />
          {fieldType === "percent" ? (
            <span className="text-sm font-medium text-slate-500" aria-hidden="true">
              %
            </span>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="offer-module-builder__field">
      <label htmlFor={question.id}>{labelWithUnit}</label>
      <div className="offer-module-builder__field-row">
        <input
          id={question.id}
          aria-label={labelWithUnit}
          type="text"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

/**
 * TPL-001 — dialog-først veiviser når en kalkylemal lastes.
 * Bruker svarer på globals + ulåste User Insert-felter før malen kopieres inn.
 */
export default function OfferTemplateWizardDialog({
  open,
  onClose,
  templateCalc,
  onConfirm,
}) {
  const { t } = useTranslation();
  const questions = useMemo(
    () => (open ? extractTemplateWizardQuestions(templateCalc, t) : []),
    [open, templateCalc, t]
  );
  const questionSections = useMemo(() => {
    const sections = [];
    const indexByKey = new Map();
    questions.forEach((q) => {
      const key =
        q.calcId != null
          ? `calc:${q.calcId}`
          : q.calcTitle
            ? `title:${q.calcTitle}`
            : "root";
      if (!indexByKey.has(key)) {
        indexByKey.set(key, sections.length);
        sections.push({
          key,
          title: q.calcTitle || t("offers.template_wizard.root_section"),
          questions: [],
        });
      }
      sections[indexByKey.get(key)].questions.push(q);
    });
    return sections;
  }, [questions, t]);
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    if (!open) return;
    const initial = {};
    questions.forEach((q) => {
      initial[q.id] = q.value ?? "";
    });
    setAnswers(initial);
  }, [open, questions]);

  const handleConfirm = () => {
    const next = applyWizardAnswersToCalculation(templateCalc, answers);
    onConfirm?.(next);
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      size="lg"
      title={t("offers.template_wizard.title")}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        <p className="text-sm text-slate-600">{t("offers.template_wizard.hint")}</p>
        {!questions.length ? (
          <p className="text-sm text-slate-500">{t("offers.template_wizard.empty")}</p>
        ) : (
          <div className="min-h-0 flex-1 space-y-4 overflow-auto rounded-lg border border-slate-200 p-3">
            {questionSections.map((section) => (
              <section key={section.key} className="space-y-3">
                {questionSections.length > 1 ? (
                  <h3 className="sticky top-0 z-[1] border-b border-slate-100 bg-white pb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                    {section.title}
                  </h3>
                ) : null}
                <ul className="space-y-3">
                  {section.questions.map((q) => (
                    <li key={q.id}>
                      <WizardFieldControl
                        question={q}
                        value={answers[q.id]}
                        onChange={(value) =>
                          setAnswers((prev) => ({ ...prev, [q.id]: value }))
                        }
                        t={t}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
          <button
            type="button"
            className="rounded-md border border-slate-300 px-3 py-1.5 text-sm"
            onClick={onClose}
          >
            {t("offers.presentation.cancel")}
          </button>
          <button
            type="button"
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-white"
            style={{ background: ACCENT }}
            onClick={handleConfirm}
          >
            {t("offers.template_wizard.apply")}
          </button>
        </div>
      </div>
    </PMDialog>
  );
}
